import { Card, Box, Flex, Grid, Text } from 'core/components';
import styled, { css, keyframes } from 'styled-components';

export const HASH_HIGHLIGHT_CLASS_NAME = 'kz-anchor-highlight';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInUpStep = keyframes`
  from {
    opacity: 0;
    transform: translateY(28px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const hashHighlight = keyframes`
  0% {
    box-shadow: 0 0 0 0 ${({ theme }) => theme.colors.primaryAlpha || 'rgba(19, 124, 189, 0.4)'};
    background-color: ${({ theme }) => theme.backgrounds.alternate};
  }
  70% {
    box-shadow: 0 0 0 8px rgba(0, 0, 0, 0);
    background-color: ${({ theme }) => theme.backgrounds.alternate};
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
    background-color: transparent;
  }
`;

const flashSuccess = keyframes`
  0% {
    background: ${({ theme }) => theme.colors.successBackground || '#e6f9ed'};
    border-color: ${({ theme }) => theme.colors.success || '#2ecc40'};
  }
  60% {
    background: ${({ theme }) => theme.colors.successBackground || '#e6f9ed'};
    border-color: ${({ theme }) => theme.colors.success || '#2ecc40'};
  }
  100% {
    background: inherit;
    border-color: inherit;
  }
`;

const scaleSuccessIcon = keyframes`
  0% {
    transform: scale(1);
  }
  30% {
    transform: scale(1.35);
  }
  60% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideDown = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(100%);
  }
`;

export const ChatContainer = styled.div`
  background-color: ${({ isCopilotMode, isExportMode, theme }) => {
    if (isExportMode) {
      return 'transparent';
    }
    if (isCopilotMode) {
      return 'transparent';
    }
    return theme.backgrounds.app;
  }};
  display: flex;
  flex-direction: column;
  gap: 16px; // gap between messages
  padding: 0px 12px;
  padding-top: 24px;
  padding-bottom: ${({ isExportMode }) => (isExportMode ? '0px' : '50px')};
  width: 100%;
  max-width: ${({ isCopilotMode }) => (isCopilotMode ? 'auto' : '800px')};
  min-height: ${({ isExportMode }) => (isExportMode ? 'auto' : '100%')};
  // grow with the messages rather than shrinking to the scroll area and letting them overflow, which would drop the
  // bottom padding and leave the last message or thinking indicator against the prompt input
  flex-shrink: 0;
`;

export const ThinkingContainer = styled.div`
  margin-bottom: 12px;
  gap: 8px;
  margin-left: 30px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const ShimmerThinkingIndicator = styled.div`
  font-size: 13px;
  font-weight: 400;
  background: ${({ theme }) =>
    `linear-gradient(to right, ${theme.colors.body} 30%, #ed698a 45%, #76aace 50%, #ff8c2d 55%, ${theme.colors.body} 70%)`};
  background-size: 200% 100%;
  animation: gradientAnimation 2s linear infinite;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @keyframes gradientAnimation {
    0% {
      background-position: 200% 50%;
    }
    100% {
      background-position: 0 50%;
    }
  }
`;

export const MessagesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  scroll-snap-align: start;
  gap: 24px; // gap between user message and agent messages
  opacity: ${({ isExportMode }) => (isExportMode ? 1 : 0)};
  animation: ${({ isExportMode }) =>
    isExportMode
      ? 'none'
      : css`
          ${fadeInUp} 320ms cubic-bezier(0.33, 1, 0.68, 1) both
        `};
  will-change: ${({ isExportMode }) => (isExportMode ? 'auto' : 'opacity, transform')};

  &:last-child {
    padding-bottom: ${({ isExportMode }) => (isExportMode ? '0px' : '60px')};
  }

  &.${HASH_HIGHLIGHT_CLASS_NAME} {
    border-radius: 8px;
    animation: ${hashHighlight} 2s ease-out;
  }
`;

export const MessageItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const MessageBubbleBase = styled.div`
  /* padding: 10px 15px; */
  max-width: 80%;
  word-wrap: break-word;
`;

export const UserMessageBubble = styled(MessageBubbleBase)`
  background-color: ${({ theme }) => theme.colors.primary};
  border-radius: 16px 16px 2px 16px;
  padding: 10px 16px;
  color: #fff;
  margin-left: auto;
  align-self: flex-end;
  display: flex;
  flex-direction: column;
  gap: 4px;

  // make sure links like www.google.com are not blue.
  a {
    color: inherit;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const AgentMessageBubble = styled(MessageBubbleBase)`
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .answer-link-button {
    opacity: 0.65;
    transition: opacity 180ms ease;
  }

  &:hover .answer-link-button,
  &:focus-within .answer-link-button {
    opacity: 1;
  }

  &.${HASH_HIGHLIGHT_CLASS_NAME} {
    border-radius: 8px;
    animation: ${hashHighlight} 2s ease-out;
  }
`;

export const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 13px;
  flex-wrap: wrap;
  color: ${({ isUser, theme }) => (isUser ? '#fff' : theme.colors.body)};
  gap: 8px;
`;

export const Timestamp = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${({ isUser, theme }) => (isUser ? 'rgba(255, 255, 255, 0.65)' : theme.colors.muted)};
`;

export const ProcessingMessage = styled.div`
  font-style: italic;
  color: ${({ theme }) => theme.colors.body};
  padding: 5px 0;
`;

export const ReasoningDetails = styled.div`
  font-size: 0.85em;
  background-color: ${({ theme }) => theme.backgrounds.alternate};
  border-radius: 4px;
  padding: 8px;
  opacity: 0;
  animation: ${fadeInUp} 320ms cubic-bezier(0.33, 1, 0.68, 1) both;
  will-change: opacity, transform;
`;

export const ReasoningContent = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.body};
`;

export const ClampedReasoning = styled.div`
  ${({ $clamped }) =>
    $clamped &&
    css`
      max-height: 150px;
      overflow: hidden;
      -webkit-mask-image: linear-gradient(to bottom, #000 65%, transparent 100%);
      mask-image: linear-gradient(to bottom, #000 65%, transparent 100%);
    `}
`;

/* The question the main agent handed to the sub-agent */
export const SubAgentRequestBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  /* padding: 8px 12px; */
  margin-bottom: 12px;

  p:last-child {
    margin-bottom: 0;
  }
`;

// Errors get their own line under the title: raw errors shown to sudo users can run to many lines,
// and in the header row they squeezed the title. Clamped to keep the card compact.
export const ToolCallError = styled(Text)`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
  margin-left: 24px;
`;

export const ToolCallCard = styled(Card)`
  padding: 16px;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  flex-wrap: wrap;
  gap: 8px;
  transition: border-radius 200ms ease-out;

  /* Remove bottom border radius when expanded to merge visually with ToolResultCard below */
  &.expanded {
    border-radius: 8px 8px 0 0;
  }

  &.success.flash {
    animation: ${flashSuccess} 900ms cubic-bezier(0.33, 1, 0.68, 1);
  }

  .bp4-icon.success-icon {
    animation: ${scaleSuccessIcon} 600ms cubic-bezier(0.33, 1, 0.68, 1);
    will-change: transform;
  }
`;

export const SubAgentPanelCard = styled(Card)`
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  /* Match the chat column so the transcript reads at the same measure */
  width: 100%;
  max-width: 800px;
  align-self: center;

  /* Blend into the panel gutter so the drawer reads like the chat side */
  &,
  &.bp4-card {
    background: ${({ theme }) => theme.backgrounds.app};
    box-shadow: none;
  }
`;

export const SubAgentPanelHeader = styled(Flex)`
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: ${({ theme }) => theme.borders.thin};
`;

export const StepContainer = styled.div`
  border-radius: 4px;
  opacity: 0;
  animation: ${fadeInUpStep} 600ms cubic-bezier(0.33, 1, 0.68, 1) both;
  will-change: opacity, transform;
`;

export const StepHeader = styled.div`
  font-weight: 500;
  font-size: 13px;
`;

export const FancyInput = styled(Box)`
  padding: 0 12px 12px 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  flex-shrink: 0;

  &:before {
    content: '';
    position: absolute;
    top: -30px;
    bottom: 0;
    inset-inline-start: 0;
    width: 100%;
    height: 85px;
    pointer-events: none;
    background: ${(props) => `linear-gradient(rgba(255, 255, 255, 0), ${props.theme.colors.decks.background} 60%)`};
  }
`;

export const LargeHeading = styled(Flex)`
  font-size: 52px;
  font-weight: 700;

  text-align: center;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  user-select: none;
  pointer-events: none;
  background: linear-gradient(270deg, #00c6ff, #0072ff, #b721ff, #fdbb2d);
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

export const KaizenSidebar = styled(Box)`
  background: ${(props) => props.theme.colors.cardBackground};
  display: flex;
  flex-direction: column;
  overflow: auto;
  scrollbar-width: thin;
  /* scales with the viewport, within bounds, until the user resizes it */
  flex-shrink: 0;
  width: 18vw;
  min-width: 220px;
  max-width: 420px;
  height: calc(100vh - 100px);

  font-size: 13px;

  .bp4-menu {
    min-width: 0;
    background: transparent;
  }
`;

export const KaizenLayout = styled(Grid)`
  transition:
    grid-template-columns 500ms cubic-bezier(0.33, 1, 0.68, 1),
    background 300ms;
  will-change: grid-template-columns;
`;

export const fadeInGridContent = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const ChatLayout = styled(Grid)`
  display: grid;
  transition:
    grid-template-columns 500ms cubic-bezier(0.33, 1, 0.68, 1),
    background 300ms;
  will-change: grid-template-columns;

  & > * {
    opacity: 0;
    animation: ${fadeInGridContent} 400ms cubic-bezier(0.33, 1, 0.68, 1) both;
  }

  & > *:not(:first-child) {
    animation-delay: 120ms;
  }
`;

export const KaizenScrollArea = styled(Flex)`
  align-items: center;
  flex-direction: column;
  flex: 1;
  gap: 12px;
  flex-grow: 1;
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => `${theme.colors.scrollbarThumb} ${theme.colors.scrollbarTrack}`};

  > * {
    scrollbar-width: thin;
  }
`;

export const KaizenToolResultPanelWrapper = styled(Flex)`
  flex-direction: column;
  flex: 1;
  gap: 12px;
  /* height: 100%; */
  height: calc(100vh - 100px);
  overflow: auto;
  padding: 12px;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => `${theme.colors.scrollbarThumb} ${theme.colors.scrollbarTrack}`};

  > * {
    scrollbar-width: thin;
  }
`;

export const SmallHeading = styled(Text)`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.muted};
`;

export const ScrollToBottomButton = styled.div`
  position: absolute;
  bottom: 24px;
  left: 24px;
  z-index: 10000;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  animation: ${({ isVisible }) => (isVisible ? slideUp : slideDown)} 200ms cubic-bezier(0.33, 1, 0.68, 1);
  pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};

  .bp4-button {
    border-radius: 100% !important;
    width: 48px;
    height: 48px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    .bp4-icon {
      margin: 0;
    }
  }
`;
